import React, { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";

function KYCStatusBadge({ status }) {
  const key = String(status || "").toLowerCase();

  const styles = {
    approved: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    pending: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
    rejected: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
        styles[key] || "bg-gray-50 text-gray-700 ring-1 ring-gray-200"
      }`}
    >
      {status || "N/A"}
    </span>
  );
}

function LoadingRow() {
  return (
    <tr className="border-t border-gray-200 dark:border-gray-700">
      {Array.from({ length: 5 }).map((_, i) => (
        <td key={i} className="py-4 px-4">
          <div className="h-4 w-full max-w-[220px] rounded bg-gray-100 dark:bg-gray-700 animate-pulse" />
        </td>
      ))}
    </tr>
  );
}

export default function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setErrorMsg("");

      try {
        const res = await axios.get(`/api/admin/users`, {
          params: { page, q: searchQuery },
        });

        setUsers(res.data?.data || []);
        setTotalPages(res.data?.pagination?.totalPages || 1);
      } catch (err) {
        const status = err.response?.status;
        const message = err.response?.data?.message || err.message;

        console.error(
          "❌ Erreur fetch users:",
          err.response?.data || err.message,
        );

        if (status === 401) {
          navigate("/login");
          return;
        }
        if (status === 403) {
          setErrorMsg("Accès refusé (permissions insuffisantes).");
          return;
        }

        setErrorMsg(message || "Erreur lors du chargement des utilisateurs.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery]);

  const canGoPrev = page > 1;
  const canGoNext = page < totalPages;

  const subtitle = useMemo(() => {
    if (loading) return "Chargement…";
    return `${users.length} utilisateur(s) affiché(s)`;
  }, [loading, users.length]);

  return (
    <div className="pt-6 px-4 md:ml-64 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
              Users
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {subtitle}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative w-full sm:w-80">
              <input
                type="text"
                placeholder="Search by email or phone"
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm text-gray-900 dark:text-gray-100"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                setPage(1);
              }}
              className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Error */}
        {errorMsg ? (
          <div className="rounded-2xl border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-900/20 px-4 py-3 text-rose-700 dark:text-rose-200 text-sm">
            {errorMsg}
          </div>
        ) : null}

        {/* Table Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/70 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-900/40 sticky top-0 z-10">
                <tr className="text-left">
                  <th className="py-3 px-4 text-xs font-semibold tracking-wide text-gray-600 dark:text-gray-300">
                    Full name
                  </th>
                  <th className="py-3 px-4 text-xs font-semibold tracking-wide text-gray-600 dark:text-gray-300">
                    Email
                  </th>
                  <th className="py-3 px-4 text-xs font-semibold tracking-wide text-gray-600 dark:text-gray-300">
                    Phone
                  </th>
                  <th className="py-3 px-4 text-xs font-semibold tracking-wide text-gray-600 dark:text-gray-300">
                    KYC
                  </th>
                  <th className="py-3 px-4 text-right text-xs font-semibold tracking-wide text-gray-600 dark:text-gray-300">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <>
                    <LoadingRow />
                    <LoadingRow />
                    <LoadingRow />
                    <LoadingRow />
                    <LoadingRow />
                  </>
                ) : users.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center py-10 text-sm text-gray-500 dark:text-gray-400"
                    >
                      No users found.
                    </td>
                  </tr>
                ) : (
                  users.map((user) => {
                    const fullName =
                      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                      `${user.profile?.firstName || ""} ${user.profile?.lastName || ""}`.trim() ||
                      "N/A";

                    return (
                      <tr
                        key={user.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                      >
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {fullName}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            ID: {user.id}
                          </div>
                        </td>

                        <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-200">
                          {user.email || "—"}
                        </td>

                        <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-200">
                          {user.phoneNumber || "—"}
                        </td>

                        <td className="py-3 px-4">
                          <KYCStatusBadge status={user.profile?.kycStatus} />
                        </td>

                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="inline-flex items-center justify-center px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-sm font-medium"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer / Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Page{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {page}
              </span>{" "}
              of{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {totalPages}
              </span>
            </div>

            {totalPages > 1 ? (
              <div className="flex items-center gap-2">
                <button
                  disabled={!canGoPrev}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-white disabled:dark:hover:bg-gray-800"
                >
                  Previous
                </button>
                <button
                  disabled={!canGoNext}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-white disabled:dark:hover:bg-gray-800"
                >
                  Next
                </button>
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-400">—</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
